import React, { useState, useEffect, useMemo } from 'react';
import FeatureCard from './FeatureCard';
import { useFeatures } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { seoConfig } from '../../lib/seo';
import LoadingSpinner from '../ui/LoadingSpinner';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import '../../styles/components/masonry-grid.css';

const Features: React.FC = () => {
  useCanonicalUrl(seoConfig.features);

  // Fetch dynamic features from Supabase
  const { data: features, loading: featuresLoading, error: featuresError } = useFeatures();

  // Create breadcrumbs for features page
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Features', isActive: true }
  ];
  
  // Debug logging
  useEffect(() => {
    console.log('Features Debug Info:');
    console.log('- features:', features);
    console.log('- loading:', featuresLoading);
    console.log('- error:', featuresError);
    if (features) {
      console.log('- features count:', features.length);
      console.log('- features data:', features);
    }
  }, [features, featuresLoading, featuresError]);
  
  // Filter only published and publicly visible features
  const publishedFeatures = useMemo(() => {
    const filtered = features?.filter(feature => feature.is_published && feature.public_visibility) || [];
    console.log('- publishedFeatures:', filtered);
    console.log('- publishedFeatures count:', filtered.length);
    return filtered;
  }, [features]);

  // Group features by category, sorted alphabetically
  const featuresByCategory = useMemo(() => {
    const grouped: { [key: string]: typeof publishedFeatures } = {};
    publishedFeatures.forEach(feature => {
      const category = feature.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(feature);
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [publishedFeatures]);

  // Helper function to distribute features into 4 columns for masonry
  const distributeToColumns = (items: typeof publishedFeatures) => {
    const columns: typeof publishedFeatures[] = [[], [], [], []];
    items.forEach((item, index) => {
      columns[index % 4].push(item);
    });
    return columns;
  };

  // Mobile detection for layout switching
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show loading when we don't have any features data yet
  if (featuresLoading && !features) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading features..." />
        </main>
      </div>
    );
  }

  return (
    <div className="page-container" style={{maxWidth: '1336px'}}>
      <main className="page-main">
        <div className="page-content">
          <Breadcrumbs items={breadcrumbs} />
          
          <header className="page-header">
            <h1>Features</h1>
            <div className="page-meta">
              <p>Discover features that make LUT Rolls the perfect choice for photo editing on iPhone</p>
            </div>
          </header>

          <div className="features-content">

            {featuresLoading && (
              <div className="loading-card">
                <div className="card-content">
                  <p>Loading features...</p>
                </div>
              </div>
            )}

            {!featuresLoading && publishedFeatures.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="card-content">
                  <h3>No Features Available</h3>
                  <p>Features will appear here once they are added to the database.</p>
                  <p style={{ fontSize: '0.9em', opacity: 0.7 }}>
                    Add features to your Supabase 'features' table to see them displayed here.
                  </p>
                </div>
              </div>
            )}

            {featuresByCategory.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                {featuresByCategory.map(([category, categoryFeatures]) => (
                  <div key={category}>
                    <h2 style={{
                      fontSize: '1.75rem',
                      marginBottom: '1.5rem',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      paddingBottom: '0.5rem'
                    }}>
                      {category}
                    </h2>
                    {isMobile ? (
                      // Mobile: Use simple vertical layout
                      <div className="mobile-cards-container">
                        {categoryFeatures.map((feature) => (
                          <div key={feature.id} className="mobile-card-wrapper">
                            <FeatureCard feature={feature} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Desktop/Tablet: Masonry layout with 4 columns
                      <div className="css-masonry-container features-grid">
                        {distributeToColumns(categoryFeatures).map((column, colIndex) => (
                          <div key={colIndex} className="css-masonry-column">
                            {column.map((feature) => (
                              <div key={feature.id} className="css-masonry-item">
                                <FeatureCard feature={feature} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Features;